import { GRAPHICS_CATEGORY_ID } from '@/utilities/consts';
import {
  CATEGORY_EDITORIAL_BY_SLUG, CATEGORY_VERSION_BY_SLUG, FPS_BY_SLUG, freshCategoryFilters, ORIENTATION_BY_SLUG,
  PEOPLE_BY_SLUG,
  RESOLUTION_BY_SLUG
} from './category-repo';
import {
  DATE_ADDED_DEFAULT_VALUE, isValidDateAdded,
  isValidSortBy, SORT_BY_DEFAULT_VALUE
} from './filter-meta';

function convertOldSpecValue(str) {
  const array = str.split('|');
  const filtered = array.filter(_str => _str !== 'none');

  if (filtered.length) {
    return filtered[filtered.length - 1];
  }

  return undefined;
}

function splitOnFirst(str, splitOn) {
  const before = str.substr(0, str.indexOf(splitOn));
  const after = str.substr(str.indexOf(splitOn) + 1);

  return {
    before,
    after,
  };
}

function specToObject(specString) {
  const parts = splitOnFirst(specString, ':');
  const key = parts.before;
  const value = parts.after;

  return {
    spec: key,
    value,
  };
}

function decodeURLParams(search) {
  const hashes = search.slice(search.indexOf('?') + 1).split('&');
  return hashes.reduce((params, hash) => {
    const split = hash.indexOf('=');

    if (split < 0) {
      return Object.assign(params, {
        [hash]: null,
      });
    }

    const key = hash.slice(0, split);
    const val = hash.slice(split + 1);

    return Object.assign(params, { [key]: decodeURIComponent(val) });
  }, {});
}

export default function urlToState(url, categorySlug) {
  const query = decodeURLParams(url);
  const out = {
    sortBy: SORT_BY_DEFAULT_VALUE,
    dateAdded: DATE_ADDED_DEFAULT_VALUE,
    searchTags: [],
  };

  if (isValidDateAdded(query.date_added)) {
    out.dateAdded = query.date_added;
  }

  if (isValidSortBy(query.sort_by)) {
    out.sortBy = query.sort_by;
  }

  const categoryFilters = freshCategoryFilters(categorySlug);
  out.categoryFilters = categoryFilters;

  if (query.subcategories) {
    const urlSubCategories = query.subcategories.split(',');
    urlSubCategories.forEach((subCategory) => {
      // set state.isFree to true if subcategory is free
      if (!out.isFree && subCategory === 'free') {
        out.isFree = true;
      }
      categoryFilters.selected = true;
      const filterSubCategories = categoryFilters.subCategories;
      const exists = Object.keys(filterSubCategories).includes(subCategory);
      if (exists) {
        filterSubCategories[subCategory].selected = true;
      }
    });
  }

  if (query.filetypes) {
    const urlFileTypes = query.filetypes.split(',');
    urlFileTypes.forEach((fileType) => {
      if (!out.categoryFilters.isVector && fileType === 'vector') {
        out.categoryFilters.isVector = true;
      }
      const filterFileTypes = categoryFilters.fileTypes;
      const exists = Object.keys(filterFileTypes).includes(fileType);
      if (exists) {
        filterFileTypes[fileType].selected = true;
      }
    });
  }

  if (query.applicationsSupported) {
    const urlApplicationsSupported = query.applicationsSupported.split(',');
    urlApplicationsSupported.forEach((application) => {
      const filterApplicationsSupported = categoryFilters.applicationsSupported;
      const exists = Object.keys(filterApplicationsSupported).includes(application);
      if (exists) {
        filterApplicationsSupported[application].selected = true;
      }
    });
  }

  if (query.themes) {
    const urlThemes = query.themes.split(',');
    urlThemes.forEach((theme) => {
      const filterThemes = categoryFilters.themes;
      const exists = Object.keys(filterThemes).includes(theme);
      if (exists) {
        filterThemes[theme].selected = true;
      }
    });
  }

  if (query.artstyles) {
    const urlArtStyles = query.artstyles.split(',');
    urlArtStyles.forEach((artStyle) => {
      const filterArtStyles = categoryFilters.artStyles;
      const exists = Object.keys(filterArtStyles).includes(artStyle);
      if (exists) {
        filterArtStyles[artStyle].selected = true;
      }
    });
  }

  if (query.specs) {
    const specs = query.specs.split(',');
    const specObjects = specs.map(spec => specToObject(spec));

    specObjects.forEach(({ spec, value }) => {
      if (!value) {
        return;
      }

      if (categoryFilters.hasResolutions) {
        if (spec === 'resolution') {
          categoryFilters.resolution = [];
          const selectedResolutionSlugs = value.split('|').filter(v => v);
          selectedResolutionSlugs.forEach(selectedResolutionSlug => {
            const resolutionSlug = convertOldSpecValue(selectedResolutionSlug);
            const resolution = RESOLUTION_BY_SLUG[resolutionSlug];
            if (resolution) {
              categoryFilters.resolution.push(resolution.slug);
            }
          })
        }
      }

      if (categoryFilters.hasResolutions || categoryFilters.id === GRAPHICS_CATEGORY_ID) {
        if (spec === 'orientation') {
          categoryFilters.orientation = [];
          const selectedOrientationSlugs = value.split('|').filter(v => v);
          selectedOrientationSlugs.forEach(selectedOrientationSlug => {
            const orientationSlug = convertOldSpecValue(selectedOrientationSlug);
            const orientation = ORIENTATION_BY_SLUG[orientationSlug];
            if (orientation) {
              categoryFilters.orientation.push(orientation.slug);
            }
          })
        }
      }

      if (categoryFilters.hasFps) {
        if (spec === 'fps') {
          const fpsSlug = convertOldSpecValue(value);
          const fps = FPS_BY_SLUG[fpsSlug];
          if (fps) {
            categoryFilters.fps = fps;
          }
        }
      }

      if (categoryFilters.hasVersions) {
        if (spec === 'version') {
          categoryFilters.version = [];
          const selectedVersionSlugs = value.split('|').filter(v => v);
          selectedVersionSlugs.forEach(selectedVersionSlug => {
            const versionSlug = convertOldSpecValue(selectedVersionSlug);
            const version = CATEGORY_VERSION_BY_SLUG[categoryFilters.slug][versionSlug];
            if (version) {
              categoryFilters.version.push(version.slug);
            }
          })
        }
      }

      if (categoryFilters.id === GRAPHICS_CATEGORY_ID) {
        if (spec === 'people') {
          const peopleSlug = convertOldSpecValue(value);
          const p = PEOPLE_BY_SLUG[peopleSlug];
          if (p) {
            categoryFilters.people = p;
          }
        }
      }

      if (categoryFilters.hasBpms) {
        if (spec === 'bpms') {
          categoryFilters.bpms = value.split('|').filter(v => v);
        }

        if (spec === 'durations') {
          categoryFilters.durations = value.split('|').filter(v => v);
        }

        if (spec === 'proaffiliated') {
          categoryFilters.proAffiliated = value === 'exclude' ? -1 : 1;
        }
      }

      if (categoryFilters.hasRightsManagers) {
        if (spec === 'rightsManager') {
          categoryFilters.rightsManager = value === 'exclude' ? -1 : 1;
        }
      }

      if (categoryFilters && categoryFilters.hasEditorials) {
        const editorialSlug = CATEGORY_EDITORIAL_BY_SLUG[categorySlug];
        const editorial = editorialSlug[value];

        if (editorial) {
          categoryFilters.editorial = editorial;
        }
      }
    });
  }

  if (query.q) {
    out.searchTags = query.q.split(',');
  }

  if (query.isolated) {
    out.categoryFilters.isIsolated = true;
  }

  if (query.free) {
    out.isFree = true;
  }

  if (query.template_difficulty_level) {
    out.categoryFilters.templateDifficultyLevel = query.template_difficulty_level;
  }

  let page = parseInt(query.page, 10);
  if (Number.isNaN(page)) {
    page = 1;
  }
  out.productsCurrentPage = page;
  return out;
}
